import isNil from "lodash/isNil";
import { useMemo } from "react";
import type { ProductResponse } from "../../admin/products/productsApi";
import type { Currency } from "../../data/BaseEntity";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import { getOptionHint } from "../../sharedComponents/products/optionHints";
import type { SelectedProductOption } from "../../sharedComponents/products/price/getOptionPrice";
import { getOptionPrice } from "../../sharedComponents/products/price/getOptionPrice";
import type { CalculationProduct } from "../calculationTypes";
import type { PricesByDatesProductsAndOptions } from "./useProductsSellingPrices";

export interface CalculationProductWithPrices {
  readonly id: string;
  readonly options: SelectedProductOption[];
  readonly date: string;
  readonly daysShift?: number;
  readonly price: Price;
  readonly optional: boolean | undefined;
}

export function useProductsWithPrices(
  calculationProducts: (CalculationProduct & { optional?: boolean })[],
  currency: Currency | undefined,
  margin: number | undefined,
  availableProducts: ProductResponse[],
  prices: PricesByDatesProductsAndOptions,
  roundProductPrice?: boolean,
) {
  const productsWithPrices = useMemo(
    () =>
      calculationProducts.map((p) => {
        const product = availableProducts.find((pr) => pr.id === p.id);
        const productExchangeRate = product?.currency === currency ? 1 : undefined;
        const options = p.options.map((o) => {
          const { price, availableQuantity } = (prices[p.date] ?? {})[p.id]?.[o.id] ?? {};
          return {
            id: o.id,
            quantity: o.quantity,
            price: o.price,
            priceListPrice: getOptionPrice({ id: o.id }, price, productExchangeRate, margin ?? 0),
            order: getOptionOrder({ id: o.id }, product ?? null),
            availableQuantity,
          };
        });
        return {
          id: p.id,
          date: p.date,
          options: options.map((o) => ({
            ...o,
            hint: getOptionHint(o, currency, productExchangeRate),
          })),
          price: getProductPrice(options, margin ?? 0, roundProductPrice ?? true),
          daysShift: p.daysShift,
          optional: p.optional,
        };
      }),
    [calculationProducts, currency, margin, availableProducts, prices],
  );
  return { productsWithPrices };
}

function getProductPrice(
  options: { price: PriceValue | undefined; priceListPrice: Price; quantity: number }[],
  margin: number,
  roundProductPrice: boolean,
): Price {
  if (options.some((o) => isNil(o.price) && isNil(o.priceListPrice))) {
    return new Price("ON_DEMAND");
  }
  return options
    .map((o) => {
      //18185
      const optionPrice = isNil(o.price) ? o.priceListPrice : new Price(o.price);
      const withMargin = optionPrice.withMargin(margin);
      const rounded = roundProductPrice ? withMargin.round() : withMargin;
      return rounded.multiply(o.quantity);
    })
    .reduce((prev, curr) => prev.add(curr), new Price(0));
}

export function getOptionOrder(
  selectedOption: Omit<SelectedProductOption, "quantity">,
  product: ProductResponse | null,
): number | null {
  return product?.options.find((o) => o.id === selectedOption.id)?.order ?? null;
}
